import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Col,
  Input,
  Row,
  Badge,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumbs";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import Datatable from "../../components/Common/Datatable/Datatable";
import classnames from "classnames";
import {
  fetchBeats,
  fetchDivisions,
  fetchRanges,
  fetchRanges2,
  fetchRoles,
  fetchRounds,
  fetchRounds2,
  getAllUSer,
} from "../../store/common/actions";
import UserForm from "./registerUser";
import { postLogin } from "../../helpers/backend_helper";
import { toast } from "react-toastify";
import PasswordForm from "./changePassword";
import WhatsAppMessageForm from "./whatsAppMessage";
import config from "../../config";
import { checkFilterMatch } from "../../helpers/general";

export default function ManageUser() {
  const dispatch = useDispatch();
  const commonSelector = createSelector(
    (state) => state.Common,
    (Common) => ({
      allUsers: Common.allUsersnvehicle ? Common.allUsersnvehicle : [],
    })
  );
  const { allUsers } = useSelector(commonSelector);

  useEffect(() => {
    dispatch(getAllUSer());
    dispatch(fetchRoles());
    dispatch(fetchDivisions());
    // Only try to load initial data if we have form values on first render
    const { division, range, round, beat } = formData;
    if (division) {
      dispatch(fetchRanges(division));

      if (range) {
        dispatch(fetchRounds(range));

        if (round) {
          dispatch(fetchBeats(round));
        }
      }
    }
  }, []);

  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("edit");
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [actionType, setActionType] = useState(""); // 'activate' or 'deactivate'
  const [messageType, setMessageType] = useState("");
  const [updated, setUpdated] = useState(false);
  const [whtupdated, setwhtUpdated] = useState(false);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  
  const { divisions, ranges, rounds, beats, roles } = useSelector(
    (state) => state.Common || {}
  );
  
  const [formData, setFormData] = useState({
    division: "",
    range: "",
    round: "",
    beat: "",
    role: "",
    assignedUser: "",
    status: "",
    search: "",
  });

  // Separate state for applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    division: "",
    range: "",
    round: "",
    beat: "",
    role: "",
    assignedUser: "",
    status: "",
    search: "",
  });

  // Updated filtered users with the reusable function
  const filteredUsers = React.useMemo(() => {
    if (!allUsers || !Array.isArray(allUsers)) return [];

    return allUsers.filter((user) => {
      // Division filter
      if (!checkFilterMatch(appliedFilters.division, user.divisionId)) return false;
      
      // Range filter  
      if (!checkFilterMatch(appliedFilters.range, user.rangeId)) return false;
      
      // Round filter
      if (!checkFilterMatch(appliedFilters.round, user.roundId)) return false;
      
      // Beat filter
      if (!checkFilterMatch(appliedFilters.beat, user.beatId)) return false;
      
      // Role filter
      if (!checkFilterMatch(appliedFilters.role, user.roleName)) return false;

      // Search filter (special case for text search)
      if (appliedFilters.search && appliedFilters.search !== "") {
        const searchValue = appliedFilters.search.trim().toLowerCase();
        const fullName = `${user?.firstName?.trim()?.toLowerCase() || ''} ${user?.lastName?.trim()?.toLowerCase() || ''}`;
        if (!fullName.includes(searchValue)) return false;
      }

      return true;
    });
  }, [allUsers, appliedFilters]);

  const maskIndianPhoneNumber = (number) => {
    if (!number || number.length < 10) return number;
    return number.replace(/(\d{5})(\d{5})/, "$1 $2");
  };

  const toggleModal = (user) => {
    setSelectedUser(user);
    setIsModalOpen(!isModalOpen);
    setActiveTab("edit");
    setNewPassword("");
    setPasswordError("");
    setConfirmPassword("");
  };

  const toggleDeleteModal = (user) => {
    setSelectedUser(user);
    setDeleteModalOpen(!deleteModalOpen);
  };

  const toggleStatusModal = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setStatusModalOpen(!statusModalOpen);
  };

  const handleDeleteUser = async () => {
    const payload = {
      act: "user",
      subact: "delete",
      info: { username: selectedUser.email },
    };

    try {
      const response = await postLogin(payload);
      toast.success(response?.message || "User deleted successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      setDeleteModalOpen(false);
      dispatch(getAllUSer()); // Refresh user list
    } catch (error) {
      toast.error("Error deleting user.");
      setDeleteModalOpen(false);
    }
  };

  const handleStatusChange = async () => {
    const payload = {
      act: "user",  
      subact: "inactiveUser",
      info: { username: selectedUser?.email,
        status: actionType === "activate" ? "active" : "inactive",
       },
    };

    try {
      const response = await postLogin(payload);
      setStatusModalOpen(false);
      dispatch(getAllUSer()); // Refresh user list
    } catch (error) {
      toast.error(`Error inactivating user.`);
      setStatusModalOpen(false);
    }
  };

  const handleSave = () => {
    setUpdated(true);
    setIsModalOpen(false);
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      setPasswordError("Both fields are required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    const payload = {
      act: "user",
      subact: "adminSetPasswod",
      info: { username: selectedUser.email, password: newPassword },
    };
    const response = await postLogin(payload);
    toast.success(response?.message, {
      position: "top-right",
      autoClose: 3000,
    });
    setIsModalOpen(false);
  };

  // Apply filters function
  const handleApplyFilters = () => {
    setAppliedFilters({ ...formData });
  };

  // Reset filters function
  const handleResetFilters = () => {
    const resetData = {
      division: "",
      range: "",
      round: "",
      beat: "",
      role: "",
      status: "",
      search: "",
    };
    setFormData(resetData);
    setAppliedFilters(resetData);
  };

  const columns = [
    {
      name: "Name",
      selector: (row) =>
        row.firstName.charAt(0).toUpperCase() +
        row.firstName.slice(1) +
        " " +
        row.lastName.charAt(0).toUpperCase() +
        row.lastName.slice(1),
      sortable: true,
      width: "10%",
    },
    { 
      name: "Email/Vehicle No", 
      selector: (row) => row.email, 
      sortable: true,
      width: "15%",
    },
    {
      name: "Role",
      selector: (row) =>
        row.roleName
          ? row.roleName.charAt(0).toUpperCase() +
            row.roleName.slice(1).toLowerCase()
          : "",
      sortable: true,
      width: "9%",
    },
    {
      name: "Division",
      selector: (row) => row.divisionName?.trim() || "All",
      sortable: true,
      width: "13%",
    },
    {
      name: "Range",
      selector: (row) => row?.rangeName?.trim() || "All",
      sortable: true,
      width: "9%",
    },
    {
      name: "Round",
      selector: (row) => row?.roundName?.trim() || "All",
      sortable: true,
      width: "9%",
    },
    {
      name: "Beat",
      selector: (row) => row?.beatName?.trim() || "All",
      sortable: true,
      width: "8%",
    },
    {
      name: "Phone",
      selector: (row) => {
        const rawNumber = String(row.contactNumber).replace(/\D/g, "");
        const masked = maskIndianPhoneNumber(rawNumber);
        return (
          <a
            href={`tel:${rawNumber}`}
            style={{ color: "#1a73e8", textDecoration: "none" }}
          >
            {masked}
          </a>
        );
      },
      sortable: true,
      width: "9%",
    },
    {
      name: "Status",
      selector: (row) => {
        const isActive = row.status !== "inactive";
        return (
          <Badge
            color={isActive ? "success" : "warning"}
            className="text-uppercase"
          >
            {isActive ? "Active" : "Deactivate"}
          </Badge>
        );
      },
      // sortable: true,
      width: "9%",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-1">
          <Button 
            color="info" 
            size="sm" 
            onClick={() => toggleModal(row)}
            className="btn-soft-info"
            title="Edit User"
          >
            <i className="fas fa-edit"></i>
          </Button>
          <Button
            color="danger"
            size="sm"
            disabled
            className="btn-soft-danger"
            onClick={() => toggleDeleteModal(row)}
            title="Delete User"
          >
            <i className="fas fa-trash"></i>
          </Button>
          {row.status !== "inactive" ? (
            <Button
              color="secondary"
              size="sm"
              className="btn-soft-warning"
              onClick={() => toggleStatusModal(row, "deactivate")}
              title="Deactivate User"
            >
              <i className="fas fa-user-times"></i>
            </Button>
          ) : (
            <Button
              color="primary"
              size="sm"
              className="btn-soft-success"
              onClick={() => toggleStatusModal(row, "activate")}
              title="Activate User"
            >
              <i class="mdi mdi-account-off"></i>
            </Button>
          )}
        </div>
      ),
      width: "8%",
    },
  ];

  if (!allUsers) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2">Loading users...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const dependentDropdowns = [
    {
      label: "Division",
      key: "division",
      data: divisions,
      fetch: fetchRanges2,
      icon: "fas fa-building",
    },
    {
      label: "Range",
      key: "range",
      data: ranges,
      fetch: fetchRounds2,
      icon: "fas fa-map-marked-alt",
    },
    {
      label: "Round",
      key: "round",
      data: rounds,
      fetch: fetchBeats,
      icon: "fas fa-route",
    },
    {
      label: "Beat",
      key: "beat",
      data: beats,
      fetch: null,
      icon: "fas fa-map-pin",
    },
  ];

  const updatedMessage = async (message) => {
    const url =
      messageType == "whatsapp"
        ? config.REACT_APP_WHATSAPP_URL
        : config.REACT_APP_SMS_URL;

    const payload = {
      act: messageType,
      templateName:"login_msg_new",
      phoneNumbers: message?.phoneNumbers,
      params: [`${message?.message}`],
    };
    const payload1 = {
      act: messageType,
            templateName:"login_msg_new",

      phoneNumbers: message?.phoneNumbers,
      params: message?.message,
    };

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(messageType == "whatsapp" ? payload : payload1),
    });

    const result = await response.json();
    if (result?.Result == "OK") {
      toast.success("Message sent successfully", {
        position: "top-right",
        autoClose: 3000,
      });
    } else {
      console.error("Failed to send message:", message);
      toast.error("Failed to send message", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleFormSubmit = (type) => {
    setMessageType(type);
    setwhtUpdated(true);
  };

  // Modify handleInputChange to handle role filter
  const handleInputChange = (e, key, fetchAction) => {
    const value = e.target.value;

    // Handle role filter separately
    if (key === "role" || key === "search") {
      setFormData({ ...formData, [key]: value });
      return;
    }

    // Reset dependent dropdowns
    let updatedFormData = { ...formData, [key]: value };

    if (key === "division") {
      updatedFormData = {
        ...updatedFormData,
        range: "",
        round: "",
        beat: "",
      };

      // If All is selected (value is '' or '0'), don't fetch ranges
      if (value && value !== "0") {
        // Fetch ranges for this division
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    } else if (key === "range") {
      updatedFormData = {
        ...updatedFormData,
        round: "",
        beat: "",
      };

      // If All is selected (value is '' or '0'), don't fetch rounds
      if (value && value !== "0") {
        // Fetch rounds for this range
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    } else if (key === "round") {
      updatedFormData = {
        ...updatedFormData,
        beat: "",
      };
      if (value && value !== "0") {
        // Fetch rounds for this range
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    }

    // Update form data
    setFormData(updatedFormData);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Control Room" breadcrumbItem="Manage User" />
        
        {/* Enhanced Filter Card */}
        <Card className="shadow-sm mb-4">
          {!isFiltersCollapsed && (
            <CardBody>
              <Row className="g-3">
                {dependentDropdowns?.map(({ label, key, data, fetch, icon }, index) => (
                  <Col lg={2} md={3} sm={6} key={key}>
                    <FormGroup>
                      <Label for={key} className="form-label">
                        {label}
                      </Label>
                      <Input
                        type="select"
                        id={key}
                        className="form-select"
                        value={formData[key] || ""}
                        onChange={(e) => handleInputChange(e, key, fetch)}
                        disabled={
                          (index > 0 &&
                            !formData[
                              ["division", "range", "round"][index - 1]
                            ]) ||
                          (index > 0 && formData["division"] === "0")
                        }
                      >
                        <option value="0">All {label}s</option>
                        {Array.isArray(data?.data) &&
                          data.data.map((item) => (
                            <option
                              key={item[`${key}Id`]}
                              value={item[`${key}Id`]}
                            >
                              {item.name ||
                                item.divisionName ||
                                item.rangeName ||
                                item.beatName ||
                                item.roundName}
                            </option>
                          ))}
                      </Input>
                    </FormGroup>
                  </Col>
                ))}

                {/* Role Filter */}
                <Col lg={2} md={3} sm={6}>
                  <FormGroup>
                    <Label for="role" className="form-label">
                      Role
                    </Label>
                    <Input
                      type="select"
                      id="role"
                      className="form-select"
                      value={formData.role || ""}
                      onChange={(e) => handleInputChange(e, "role", null)}
                    >
                      <option value="0">All Roles</option>
                      {roles?.map((role) => (
                        <option key={role.roleId} value={role.role}>
                          {role.role}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>

                {/* Search Filter */}
                <Col lg={2} md={3} sm={6}>
                  <FormGroup>
                    <Label for="search" className="form-label">
                      Search
                    </Label>
                    <Input
                      type="text"
                      id="search"
                      className="form-control"
                      placeholder="Search by name..."
                      value={formData.search || ""}
                      onChange={(e) => handleInputChange(e, "search", null)}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Filter Action Buttons */}
              <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                <Button
                  color="primary"
                  onClick={handleApplyFilters}
                >
                  Apply
                </Button>
                <Button
                  color="secondary"
                  onClick={handleResetFilters}
                >
                  Reset
                </Button>
              </div>
            </CardBody>
          )}
        </Card>

        {/* Data Table */}
        <Card className="shadow-sm">
          <CardBody>
            <Datatable data={filteredUsers} columns={columns} />
          </CardBody>
        </Card>
      </Container>

      {/* Edit User Modal */}
      {selectedUser && (
        <Modal isOpen={isModalOpen} toggle={toggleModal} size="xl">
          <ModalHeader toggle={toggleModal} className="bg-light">
            <div className="d-flex align-items-center">
              <i className="fas fa-user-edit text-primary me-2"></i>
              Manage User - {selectedUser.firstName} {selectedUser.lastName}
            </div>
          </ModalHeader>
          <ModalBody className="p-0">
            <div className="d-flex">
              {/* Enhanced Sidebar Navigation */}
              <Nav
                vertical
                className="border-end bg-light"
                style={{ width: "250px", minHeight: "400px" }}
              >
                <div className="p-3 border-bottom">
                  <h6 className="mb-0 text-muted">MANAGEMENT OPTIONS</h6>
                </div>
                <NavItem className="p-2">
                  <NavLink
                    className={classnames("d-flex align-items-center px-3 py-2 rounded", {
                      "bg-primary text-white": activeTab === "edit",
                      "text-dark": activeTab !== "edit"
                    })}
                    onClick={() => setActiveTab("edit")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fas fa-user-edit me-2"></i>
                    Edit Profile
                  </NavLink>
                </NavItem>
                <NavItem className="p-2">
                  <NavLink
                    className={classnames("d-flex align-items-center px-3 py-2 rounded", {
                      "bg-primary text-white": activeTab === "password",
                      "text-dark": activeTab !== "password"
                    })}
                    onClick={() => setActiveTab("password")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fas fa-lock me-2"></i>
                    Change Password
                  </NavLink>
                </NavItem>
                <NavItem className="p-2">
                  <NavLink
                    className={classnames("d-flex align-items-center px-3 py-2 rounded", {
                      "bg-primary text-white": activeTab === "whatsapp",
                      "text-dark": activeTab !== "whatsapp"
                    })}
                    onClick={() => setActiveTab("whatsapp")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fab fa-whatsapp me-2"></i>
                    Send Message
                  </NavLink>
                </NavItem>
              </Nav>

              {/* Right Content */}
              <div className="flex-grow-1 p-4">
                {activeTab === "edit" ? (
                  <UserForm
                    userData={selectedUser}
                    onsubmit={updated}
                    setUpdated={setUpdated}
                  />
                ) : activeTab === "whatsapp" ? (
                  <WhatsAppMessageForm
                    selectedUser={selectedUser}
                    whtupdated={whtupdated}
                    setwhtUpdated={setwhtUpdated}
                    onsubmit={updatedMessage}
                  />
                ) : (
                  <PasswordForm
                    setNewPassword={setNewPassword}
                    setPasswordError={setPasswordError}
                    newPassword={newPassword}
                    passwordError={passwordError}
                    setConfirmPassword={setConfirmPassword}
                    confirmPassword={confirmPassword}
                  />
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="bg-light">
            {activeTab === "edit" ? (
              <Button color="primary" onClick={handleSave} className="btn-label">
                <i className="ri-save-line label-icon align-middle fs-16 me-2"></i>
                Save Changes
              </Button>
            ) : activeTab === "whatsapp" ? (
              <>
                <Button
                  color="info"
                  disabled
                  onClick={() => handleFormSubmit("sms")}
                  className="btn-label"
                >
                  <i className="ri-message-2-line label-icon align-middle fs-16 me-2"></i>
                  Send SMS
                </Button>
                <Button
                disabled
                  color="success"
                  onClick={() => handleFormSubmit("whatsapp")}
                  className="btn-label"
                >
                  <i className="ri-whatsapp-line label-icon align-middle fs-16 me-2"></i>
                  Send WhatsApp
                </Button>
              </>
            ) : (
              <Button color="warning" onClick={handleChangePassword} className="btn-label">
                <i className="ri-lock-2-line label-icon align-middle fs-16 me-2"></i>
                Change Password
              </Button>
            )}
            <Button color="secondary" outline onClick={toggleModal}>
              <i className="ri-close-line me-1"></i>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Enhanced Delete Modal */}
      <Modal isOpen={deleteModalOpen} toggle={toggleDeleteModal} centered>
        <ModalHeader toggle={toggleDeleteModal} className="bg-danger text-white">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Confirm Delete
        </ModalHeader>
        <ModalBody className="text-center py-4">
          <i className="fas fa-trash-alt text-danger mb-3" style={{ fontSize: "48px" }}></i>
          <h5 className="mb-3">Are you sure?</h5>
          <p className="text-muted mb-0">
            You are about to delete user{" "}
            <strong className="text-danger">{selectedUser?.email}</strong>
            <br />
            This action cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDeleteUser} className="btn-label">
            <i className="ri-delete-bin-line label-icon align-middle fs-16 me-2"></i>
            Yes, Delete
          </Button>
          <Button color="secondary" outline onClick={toggleDeleteModal}>
            <i className="ri-close-line me-1"></i>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Status Change Modal */}
      <Modal isOpen={statusModalOpen} toggle={() => setStatusModalOpen(false)} centered>
        <ModalHeader 
          toggle={() => setStatusModalOpen(false)} 
          className={`${actionType === "activate" ? "bg-success" : "bg-warning"} text-white`}
        >
          <i className={`fas ${actionType === "activate" ? "fa-user-check" : "fa-user-times"} me-2`}></i>
          Confirm {actionType === "activate" ? "Activation" : "Deactivation"}
        </ModalHeader>
        <ModalBody className="text-center py-4">
          <i 
            className={`fas ${actionType === "activate" ? "fa-user-check text-success" : "fa-user-times text-warning"} mb-3`} 
            style={{ fontSize: "48px" }}
          ></i>
          <h5 className="mb-3">Are you sure?</h5>
          <p className="text-muted mb-0">
            You are about to {actionType} user{" "}
            <strong className={actionType === "activate" ? "text-success" : "text-warning"}>
              {selectedUser?.firstName} {selectedUser?.lastName}
            </strong>
            <br />
            {actionType === "activate" 
              ? "This user will be able to access the system again." 
              : "This user will no longer be able to access the system."
            }
          </p>
        </ModalBody>
        <ModalFooter>
          <Button 
            color={actionType === "activate" ? "success" : "warning"} 
            onClick={handleStatusChange} 
            className="btn-label"
          >
            <i className={`ri-${actionType === "activate" ? "check" : "close"}-line label-icon align-middle fs-16 me-2`}></i>
            Yes, {actionType === "activate" ? "Activate" : "Deactivate"}
          </Button>
          <Button color="secondary" outline onClick={() => setStatusModalOpen(false)}>
            <i className="ri-close-line me-1"></i>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}